import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { isTauri, startMicMonitor, notifyCaptureMeeting, onAppFocus } from "../lib/native";
import { createMeeting } from "../lib/api";

/**
 * Desktop-only bridge. Watches whether any app turns the mic on (e.g. a Zoom
 * call starts) and surfaces a native notification suggesting Lisn capture the
 * meeting. Tapping the notification brings the app forward — we "arm" on
 * mic-on and start the meeting on that focus, which is far more reliable on
 * macOS than notification action buttons. No-op in the browser.
 */
export default function NativeBridge() {
    const nav = useNavigate();
    const monitorRef = useRef(null);
    const unfocusRef = useRef(null);
    const armedRef = useRef(false);     // mic turned on, waiting for the user to act
    const armTimerRef = useRef(null);
    const startingRef = useRef(false);

    useEffect(() => {
        if (!isTauri()) return;

        const inCaptureSurface = () => {
            const p = window.location.pathname;
            return p.startsWith("/meetings/") || p.startsWith("/sessions/");
        };

        const startMeeting = async () => {
            if (startingRef.current || inCaptureSurface()) return;
            startingRef.current = true;
            armedRef.current = false;
            toast("Starting meeting — recording…");
            try {
                const m = await createMeeting({ title: "Untitled meeting" });
                // source "both" → system audio (the call) + mic, captured natively.
                nav(`/meetings/${m.id}`, { state: { autostart: true, source: "both" } });
            } catch {
                toast.error("Couldn't start the meeting");
            } finally {
                setTimeout(() => { startingRef.current = false; }, 1500);
            }
        };

        (async () => {
            // Tapping the notification brings the app forward → start the armed
            // meeting. We listen on every focus signal for reliability.
            unfocusRef.current = await onAppFocus(() => { if (armedRef.current) startMeeting(); });

            monitorRef.current = await startMicMonitor(async (running) => {
                // Don't disarm on a brief mic-off flicker — only the timeout
                // expires the arm, so a quick toggle before the tap is fine.
                if (!running) return;
                if (armedRef.current || startingRef.current || inCaptureSurface()) return;
                armedRef.current = true;
                if (armTimerRef.current) clearTimeout(armTimerRef.current);
                armTimerRef.current = setTimeout(() => { armedRef.current = false; }, 120000);
                // Notification (its action button also starts, as a bonus path).
                await notifyCaptureMeeting({ onCapture: startMeeting });
            });
        })();

        return () => {
            try { monitorRef.current?.stop(); } catch { /* ignore */ }
            try { unfocusRef.current?.(); } catch { /* ignore */ }
            if (armTimerRef.current) clearTimeout(armTimerRef.current);
        };
    }, [nav]);

    return null;
}
